import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { LoginForm, ProFormText } from "@ant-design/pro-components";
import { LockOutlined } from "@ant-design/icons";
import { Alert, App as AntdApp, theme } from "antd";

import { apiErrorMessage } from "@/lib/errors";
import { PasswordChecklist } from "@/components/PasswordRules";
import { passwordRules } from "@/lib/passwordPolicy";
import BrandLogo from "@/components/BrandLogo";

import { resetPassword } from "./api";

/** Set a new password from an emailed reset token. */
function ResetPasswordPage() {
  const { token } = theme.useToken();
  const { message } = AntdApp.useApp();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const resetToken = params.get("token") ?? "";
  const [submitting, setSubmitting] = useState(false);
  const [password, setPassword] = useState("");

  return (
    <div
      style={{
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        background: token.colorBgLayout,
      }}
    >
      <LoginForm<{ password: string; confirm: string }>
        logo={<BrandLogo />}
        title="Choose a new password"
        subTitle=""
        submitter={{
          searchConfig: { submitText: "Set new password" },
          submitButtonProps: { loading: submitting, disabled: !resetToken },
        }}
        onFinish={async (values) => {
          if (values.password !== values.confirm) {
            message.error("Passwords do not match");
            return false;
          }
          setSubmitting(true);
          try {
            await resetPassword(resetToken, values.password);
            message.success("Password updated — you can sign in now.");
            navigate("/login");
            return true;
          } catch (error) {
            message.error(apiErrorMessage(error, "Could not reset password"));
            return false;
          } finally {
            setSubmitting(false);
          }
        }}
        actions={<Link to="/login">Back to sign in</Link>}
      >
        {!resetToken && (
          <Alert
            type="error"
            showIcon
            style={{ marginBottom: 24 }}
            message="This reset link is missing its token. Request a new one."
          />
        )}
        <ProFormText.Password
          name="password"
          fieldProps={{
            size: "large",
            prefix: <LockOutlined />,
            onChange: (event) => setPassword(event.target.value),
          }}
          placeholder="New password"
          rules={passwordRules()}
        />
        <PasswordChecklist value={password} />
        <ProFormText.Password
          name="confirm"
          fieldProps={{ size: "large", prefix: <LockOutlined /> }}
          placeholder="Confirm new password"
          rules={[{ required: true, message: "Please confirm your password" }]}
        />
      </LoginForm>
    </div>
  );
}

export default ResetPasswordPage;
